import {
  animate,
  state,
  style,
  transition,
  trigger,
} from "@angular/animations";
import {
  AfterViewInit,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ChangeDetectionStrategy,
} from "@angular/core";
import {
  addVisibilityChangeEventListener,
  removeVisibilityChangeEventListener,
  SubscriptionContainer,
} from "@utils";
import deepEqual from "deep-equal";
import { FirebaseAnalyticsService } from "../../core";
import { CarouselItemDirective } from "./carousel-item/carousel-item.directive";

const ANIMATION_DELAY = 300;

export interface VisibleSlide {
  instanceId: string;
  slideKey: string;
}

type AnimationState =
  "left-exit" | "right-exit" | "left-enter" | "right-enter" | "inactive";

@Component({
  selector: "app-carousel",
  templateUrl: "./carousel.component.html",
  styleUrls: ["./carousel.component.scss"],
  animations: [
    trigger("slideInOut", [
      state(
        "left-exit",
        style({
          transform: "translateX(-100%)",
          visibility: "hidden",
        })
      ),
      state(
        "right-exit",
        style({
          transform: "translateX(100%)",
          visibility: "hidden",
        })
      ),
      state(
        "inactive",
        style({
          visibility: "hidden",
        })
      ),
      transition("void => left-enter", [
        style({ transform: "translateX(0%)" }),
      ]),
      transition("* => left-exit", [
        style({ transform: "translateX(0%)" }),
        animate(
          `${ANIMATION_DELAY}ms`,
          style({ transform: "translateX(-100%)" })
        ),
      ]),
      transition("* => left-enter", [
        style({ transform: "translateX(100%)", visibility: "visible" }),
        animate(`${ANIMATION_DELAY}ms`, style({ transform: "translateX(0%)" })),
      ]),
      transition("* => right-exit", [
        style({ transform: "translateX(0%)" }),
        animate(
          `${ANIMATION_DELAY}ms`,
          style({ transform: "translateX(100%)" })
        ),
      ]),
      transition("* => right-enter", [
        style({ transform: "translateX(-100%)", visibility: "visible" }),
        animate(`${ANIMATION_DELAY}ms`, style({ transform: "translateX(0%)" })),
      ]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CarouselComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() public autoScrollDelay: number = 10000;
  @Input() public hidePageIndicator: boolean = false;
  @Input() public hideControls: boolean = false;
  @Input() public hideArrowHighlights: boolean = false;
  @Input() public isHomePage: boolean = false;

  @ContentChildren(CarouselItemDirective)
  carouselItems!: QueryList<CarouselItemDirective>;

  @Output() visible = new EventEmitter<VisibleSlide>();

  previousSlideIndex = -1;
  currentSlideIndex = 0;
  slideLeft = true;
  isAnimating = false;
  animationTimeout: NodeJS.Timeout = null;
  autoScrollTimeout: NodeJS.Timeout = null;

  private subscriptions = new SubscriptionContainer();

  // The order of the keys is important here as it represents the order of the slides
  private slideKeys: string[] | null = null;
  private slideKey: string | null;
  private observer?: IntersectionObserver;

  // This is used to uniquely identify each seperate instance of a slide being presented
  private slideInstanceId = crypto.randomUUID();

  constructor(
    private analyticsService: FirebaseAnalyticsService,
    private elRef: ElementRef
  ) {}

  ngOnInit(): void {
    this.initAutoScroll();
  }

  ngAfterViewInit(): void {
    this.slideKeys = this.carouselItems.toArray().map((s) => s.key);
    this.slideKey = this.slideKeys[0];

    this.initIntersectionObserver();

    const carouselItemsSubscription = this.carouselItems.changes.subscribe({
      next: (value: QueryList<CarouselItemDirective>) => {
        const newKeys = value.toArray().map((s) => s.key);
        if (!this.slideKeys) {
          this.slideKeys = newKeys;
          return;
        }

        if (deepEqual(this.slideKeys, newKeys)) return;

        const newSlideKey = newKeys[this.currentSlideIndex];

        if (newSlideKey == this.slideKey) {
          this.slideKeys = newKeys;
          return;
        }

        this.slideKeys = newKeys;

        this.next(true);
      },
    });

    this.subscriptions.add(carouselItemsSubscription);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();

    if (this.animationTimeout) {
      clearTimeout(this.animationTimeout);
    }

    if (this.autoScrollTimeout) {
      clearInterval(this.autoScrollTimeout);
    }

    if (this.observer) {
      this.observer.disconnect();
    }

    removeVisibilityChangeEventListener(this.refreshIntersectionObserver);
  }

  private updateSlideId() {
    this.slideInstanceId = crypto.randomUUID();
  }

  public previous(): void {
    if (this.isAnimating) return;

    this.previousSlideIndex = this.currentSlideIndex;
    const previous = this.currentSlideIndex - 1;
    this.currentSlideIndex =
      previous < 0 ? this.carouselItems.length - 1 : previous;
    this.slideKey = this.slideKeys[this.currentSlideIndex];
    this.slideLeft = false;
    this.updateSlideId();
    this.restartAutoScrollInterval();
    this.startAnimationTimer();
    this.raiseEvent("swipe_left");
    this.refreshIntersectionObserver();
  }

  public next(isAutoScroll: boolean = false): void {
    if (this.isAnimating) return;

    if (this.carouselItems.length > 1) {
      this.previousSlideIndex = this.currentSlideIndex;
      const next = this.currentSlideIndex + 1;
      this.currentSlideIndex = next >= this.carouselItems.length ? 0 : next;
      this.slideKey = this.slideKeys[this.currentSlideIndex];
      this.slideLeft = true;
      this.updateSlideId();
      this.startAnimationTimer();
    }

    this.restartAutoScrollInterval();

    if (!isAutoScroll) {
      this.raiseEvent("swipe_right");
    }

    this.refreshIntersectionObserver();
  }

  public scrollTo(index: number): void {
    if (this.isAnimating || this.currentSlideIndex === index) return;

    this.previousSlideIndex = this.currentSlideIndex;
    this.currentSlideIndex = index;
    this.slideKey = this.slideKeys[this.currentSlideIndex];
    this.slideLeft = index > this.previousSlideIndex;

    this.updateSlideId();
    this.restartAutoScrollInterval();
    this.startAnimationTimer();
    this.refreshIntersectionObserver();
  }

  public raiseEvent(action): void {
    if (!this.isHomePage) return;
    this.analyticsService.raiseEvent("carousel_engagement", {
      flow: "signing_up",
      action,
    });
  }

  private getState(index: number): AnimationState {
    if (index !== this.previousSlideIndex && index !== this.currentSlideIndex) {
      return "inactive";
    }

    if (this.slideLeft) {
      if (index === this.previousSlideIndex) {
        return "left-exit";
      } else {
        return "left-enter";
      }
    } else {
      if (index === this.previousSlideIndex) {
        return "right-exit";
      } else {
        return "right-enter";
      }
    }
  }

  private startAnimationTimer(): void {
    this.isAnimating = true;
    this.animationTimeout = setTimeout(
      () => (this.isAnimating = false),
      ANIMATION_DELAY + 10
    );
  }

  private initAutoScroll(): void {
    this.autoScrollTimeout = setInterval(
      this.scroll.bind(this),
      this.autoScrollDelay
    );
  }

  private restartAutoScrollInterval(): void {
    if (this.autoScrollTimeout) {
      clearInterval(this.autoScrollTimeout);
    }
    this.initAutoScroll();
  }

  private scroll(): void {
    this.next(true);
  }

  private initIntersectionObserver() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.visible.emit({
            instanceId: this.slideInstanceId,
            slideKey: this.slideKeys[this.currentSlideIndex],
          });
        }
      });
    });

    this.observer.observe(this.elRef.nativeElement);

    addVisibilityChangeEventListener(this.refreshIntersectionObserver);
  }

  private refreshIntersectionObserver = () => {
    if (!this.observer) return;

    this.observer.disconnect();
    this.observer.observe(this.elRef.nativeElement);
  };
}
